import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, Collapse, Drawer } from 'antd';
import clsx from 'clsx';

import { UserContext } from '@/app/UserProvider';
import DownIcon from '@/assets/v2/down.svg?react';
import I18n from '@/assets/v2/i18n.svg?react';
import Logo from '@/assets/v2/logo.svg?react';
import { useResponsive } from '@/hooks/useResponsive';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { removeAuthToken } from '@/shared/lib/client';
import { useBannerStore } from '@/store/useBannerStore';
import { generateInternalSSOUrl, isInternalDomain } from '@/utils/const';
import {
  getRecommendedDownloadType,
  getSystemInfo,
  isSystemSupported,
} from '@/utils/systemDetector';
import { CustomDropdown } from '@/v2/components/CustomDropdown';
import type { DropdownMenuItem } from '@/v2/components/CustomDropdown';

export function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { downloadInfo } = useDownloadInfo();
  const { user, setUser } = useContext(UserContext);
  const [recommendedType, setRecommendedType] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isBannerVisible } = useBannerStore();

  // 移动端菜单抽屉状态
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // 移动端产品和资源折叠面板的展开状态
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // 动态高度测量（仅移动端）
  const [bannerHeight, setBannerHeight] = useState(0);
  const navFixedRef = useRef<HTMLDivElement | null>(null);
  const [isMobileSize, setIsMobileSize] = useState(window.innerWidth < 768);
  const { isMobile: isMobileMeta } = useResponsive();
  const isMobile = useMemo(() => isMobileMeta || isMobileSize, [isMobileMeta, isMobileSize]);

  useEffect(() => {
    const checkFlat = () => {
      setIsMobileSize(window.innerWidth < 768);
    };

    checkFlat();
    window.addEventListener('resize', checkFlat);
    return () => window.removeEventListener('resize', checkFlat);
  }, []);
  const measureHeights = useCallback(() => {
    const bannerEl = document.getElementById('global-banner');
    const newBannerHeight = bannerEl ? bannerEl.offsetHeight : 0;
    setBannerHeight(newBannerHeight || 0);
  }, []);

  // 提前到布局阶段进行首次测量，避免首屏抖动（仅在移动端执行动态测量）
  useLayoutEffect(() => {
    if (!isBannerVisible) {
      setBannerHeight(0);
      return;
    }

    if (!isMobile) {
      // 桌面端：Banner 高度固定 32px，无需动态测量
      setBannerHeight(32);
      return;
    }

    // 移动端：进行实时测量
    measureHeights();
    const bannerEl = document.getElementById('global-banner');

    let ro: ResizeObserver | null = null;
    if (bannerEl && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => measureHeights());
      ro.observe(bannerEl);
    }

    const handleResize = () => measureHeights();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (ro && bannerEl) ro.unobserve(bannerEl);
    };
  }, [isMobile, isBannerVisible, measureHeights]);

  // 滚动监听 - 首页时根据滚动位置显示背景色，其他页面始终显示背景色
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const scrollTop = document.documentElement.scrollTop;
        const heroTitleElement = document.getElementById('hero-title');

        // 动态获取 navbar 可见高度
        const navFixedEl = navFixedRef.current;
        const NAVBAR_HEIGHT = navFixedEl ? navFixedEl.offsetHeight : 88;
        const BANNER_HEIGHT = isBannerVisible ? (isMobile ? bannerHeight : 32) : 0;

        if (heroTitleElement) {
          const heroTitleRect = heroTitleElement.getBoundingClientRect();
          const heroTitleTop = heroTitleRect.top + scrollTop;
          // 当滚动到 hero-title 上方 12px（考虑 banner 与 navbar 高度）后，显示背景
          const threshold = heroTitleTop - 12 - NAVBAR_HEIGHT - BANNER_HEIGHT;
          setIsScrolled(scrollTop > threshold);
        } else {
          // 找不到 hero-title 时：兜底为超出一屏高度后显示

          setIsScrolled(scrollTop > NAVBAR_HEIGHT);
        }
      } else {
        // 其他页面：始终显示背景色
        setIsScrolled(true);
      }
    };

    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 初始检查
    handleScroll();

    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, isBannerVisible, bannerHeight, isMobile]);

  // 初始化时检测用户系统
  useEffect(() => {
    const systemInfo = getSystemInfo();
    const recommendedDownloadType = getRecommendedDownloadType();
    setRecommendedType(recommendedDownloadType);

    // 在开发环境下输出调试信息
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('NavBar 检测到的系统信息:', systemInfo);
      // eslint-disable-next-line no-console
      console.log('NavBar 推荐下载类型:', recommendedDownloadType);
    }
  }, []);

  function handleHome() {
    navigate('/');
  }

  // 智能下载处理函数
  const handleDownload = useCallback(async () => {
    navigate('/download');
    return;
    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      navigate('/download');
      return;
    }
  }, [downloadInfo, recommendedType, navigate]);

  // 处理不支持系统的点击事件
  const handleUnsupportedClick = useCallback(() => {
    navigate('/download');
  }, [navigate]);

  // 检查是否支持当前系统
  const systemSupported = isSystemSupported();

  // 处理登录
  const handleLogin = useCallback(() => {
    if (isInternalDomain()) {
      // 内部域名：直接跳转到 SSO 登录页
      window.location.href = generateInternalSSOUrl();
    } else {
      // 外部域名：跳转到登录页面
      sessionStorage.setItem('pendingRedirect', '/dashboard');
      navigate('/login?redirect=/dashboard');
    }
  }, [navigate]);

  // 处理退出登录
  const handleLogout = useCallback(async () => {
    try {
      // Remove local cookie
      removeAuthToken();

      // Clear user state
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');
      // Clear the pending redirect flag
      sessionStorage.removeItem('pendingRedirect');

      toast.success(t('loginSuccess.logoutSuccessful'));

      // Refresh page to recheck login status
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if backend call fails, remove local cookie
      removeAuthToken();
      setUser(null);

      // Clear the login success flag so next login will show toast again
      sessionStorage.removeItem('hasShownLoginSuccess');
      // Clear the pending redirect flag
      sessionStorage.removeItem('pendingRedirect');

      toast.error(t('loginSuccess.logoutFailed'));

      // Refresh page to recheck login status
      window.location.reload();
    }
  }, [t, setUser]);

  // 处理个人设置
  const handleSettings = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // 判断是否为内部域名
  const isInternal = isInternalDomain();

  // 用户下拉菜单配置
  const userMenuItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      {
        key: 'settings',
        title: t('navbar.settings'),
        onClick: handleSettings,
      },
    ];
    // 仅在外部域名时显示退出登录按钮
    if (!isInternal) {
      items.push({
        key: 'logout',
        title: t('loginSuccess.logoutButton'),
        onClick: handleLogout,
      });
    }
    return items;
  }, [t, handleSettings, handleLogout, isInternal]);

  // 用户下拉菜单 header
  const userMenuHeader = useMemo(
    () => ({
      title: user?.displayName || 'User',
      description: user?.email || user?.mail || '',
    }),
    [user],
  );

  const productItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      {
        key: 'ide',
        title: t('navbar.productMenu.ide'),
        description: t('navbar.productMenu.ideDesc'),
        href: '/download',
        target: '_blank',
      },
      {
        key: 'ext',
        title: t('navbar.productMenu.extension'),
        description: t('navbar.productMenu.extensionDesc'),
        href: '/download?tab=extension',
        target: '_blank',
      },
      // {
      //   key: 'cloudAgents',
      //   title: t('navbar.productMenu.cloudAgents'),
      //   description: t('navbar.productMenu.cloudAgentsDesc'),
      //   href: '/cloud',
      //   target: '_blank',
      // },
      {
        key: 'cli',
        title: t('navbar.productMenu.cli'),
        description: t('navbar.productMenu.cliDesc'),
        href: '/cli',
        target: '_blank',
      },
      // {
      //   key: 'chat',
      //   title: t('navbar.productMenu.chat'),
      //   description: t('navbar.productMenu.chatDesc'),
      //   href: '/chat',
      //   target: '_blank',
      // },
      {
        key: 'flow',
        title: t('navbar.productMenu.flow'),
        description: t('navbar.productMenu.flowDesc'),
        href: '/flow',
        target: '_blank',
      },
    ];
    if (isInternal) {
      return items;
    }
    return items.filter((item) => ['ide', 'cli'].includes(item.key));
  }, [isInternal, t]);

  const resourceItems = useMemo<DropdownMenuItem[]>(
    () =>
      (
        [
          {
            key: 'prd',
            title: t('navbar.resourceMenu.docs'),
            href: 'https://www.codeflicker.ai/docs/',
            target: '_blank',
          },
          // {
          //   key: 'demo',
          //   title: t('navbar.resourceMenu.bestPractices'),
          // },
          {
            key: 'changelog',
            title: t('navbar.resourceMenu.changelog'),
            href: 'https://www.codeflicker.ai/docs/changelog.html',
            target: '_blank',
          },
          {
            key: 'activity',
            title: t('navbar.resourceMenu.activity'),
            href: 'https://www.codeflicker.ai/docs/feats/others/angel-program.html',
            target: '_blank',
          },
          {
            key: 'aiSchool',
            title: t('navbar.resourceMenu.aiSchool'),
            href: 'https://ai-school.corp.kuaishou.com/',
            target: '_blank',
            internalOnly: true,
          },
        ] as DropdownMenuItem[]
      ).filter((item) => !item.internalOnly || isInternal),
    [isInternal, t],
  );

  const [productIsOpen, setProductIsOpen] = useState(false);
  const [resourceIsOpen, setResourceIsOpen] = useState(false);

  return (
    <div
      className={clsx(
        'fixed left-0 z-[100] w-full transition-[top] duration-300 ease-in-out',
        'top-0',
      )}
      style={{ top: isMobile && isBannerVisible ? bannerHeight : undefined }}
    >
      <div
        ref={navFixedRef}
        className={clsx(
          'relative z-10 mx-auto transition-all duration-300 ease-linear',
          'xs:h-[clamp(64px,8vw,88px)] h-16 lg:h-[88px]',
          isScrolled
            ? 'bg-background-primary backdrop-blur-[12px]'
            : 'bg-transparent backdrop-blur-none',
        )}
      >
        <div
          className={clsx(
            'text-content-primary relative mx-auto box-border flex h-full max-w-[1728px] items-center justify-between',
            'px-[28px] py-[28px] md:px-[88px]',
          )}
        >
          <div className="flex items-center gap-4 md:gap-20">
            <div
              className={clsx(
                'flex cursor-pointer items-center gap-5',
                'xs:h-[clamp(24px,4vw,32px)] h-6 lg:h-8',
              )}
              onClick={handleHome}
            >
              <Logo />
            </div>
            {/* 桌面端产品/资源下拉菜单 */}
            {!isMobile && (
              <div className="flex gap-4 sm:gap-12">
                <CustomDropdown
                  onOpenChange={setProductIsOpen}
                  items={productItems}
                  width={320}
                  className="text-sm"
                >
                  <div
                    className={`can-hover:hover:opacity-70 flex cursor-pointer items-center gap-1`}
                  >
                    {t('navbar.products')}
                    <div
                      className={`${productIsOpen && 'rotate-180'} transition-transform duration-200`}
                    >
                      <DownIcon />
                    </div>
                  </div>
                </CustomDropdown>
                <CustomDropdown
                  onOpenChange={setResourceIsOpen}
                  items={resourceItems}
                  width={160}
                  className="text-sm"
                >
                  <div className="can-hover:hover:opacity-60 flex cursor-pointer items-center gap-1">
                    {t('navbar.resources')}
                    <div
                      className={`${resourceIsOpen && 'rotate-180'} transition-transform duration-200`}
                    >
                      <DownIcon />
                    </div>
                  </div>
                </CustomDropdown>
                {/* 价格链接 - 仅对外域名显示 */}
                {/* {!isInternal && (
                  <div
                    className="can-hover:hover:opacity-70 flex cursor-pointer items-center text-sm"
                    onClick={() => navigate('/pricing')}
                  >
                    {t('navbar.pricing')}
                  </div>
                )} */}
              </div>
            )}
          </div>

          {/* 移动端导航 - 只显示语言切换和菜单按钮 */}
          {isMobile && (
            <div className="flex items-center gap-4">
              <div
                className="text-content-primary flex cursor-pointer items-center gap-1 text-sm"
                onClick={() => {
                  const newLang = i18n.language === 'zh' ? 'en' : 'zh';
                  i18n.changeLanguage(newLang);
                }}
              >
                <I18n />
                {i18n.language === 'zh' ? '中' : 'EN'}
              </div>
              {/* 汉堡菜单按钮 */}
              <div
                className="flex size-6 cursor-pointer items-center justify-center"
                onClick={() => setMobileMenuOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="14"
                  viewBox="0 0 20 14"
                  fill="none"
                >
                  <path
                    d="M1 1H19M1 7H19M1 13H19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* 桌面端导航 */}
          {!isMobile && (
            <div className="flex items-center gap-4">
              <div
                className="text-content-primary flex w-12 cursor-pointer items-center justify-center gap-1"
                onClick={() => {
                  const newLang = i18n.language === 'zh' ? 'en' : 'zh';
                  i18n.changeLanguage(newLang);
                }}
              >
                <I18n />
                {i18n.language === 'zh' ? '中' : 'EN'}
              </div>
              {/* 用户头像或登录按钮 */}
              {user ? (
                <CustomDropdown
                  items={userMenuItems}
                  trigger={['click']}
                  placement="bottomRight"
                  width={260}
                  header={userMenuHeader}
                >
                  <div className="flex cursor-pointer items-center">
                    <Avatar
                      src={user.avatar}
                      size={32}
                      shape="circle"
                      style={{
                        fontSize: '16px',
                        lineHeight: '16px',
                        fontWeight: 600,
                        color: '#32EDDA',
                        border: 'none',
                      }}
                    >
                      {user.displayName?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </div>
                </CustomDropdown>
              ) : !isInternal ? (
                <div
                  className={clsx(
                    'h-8 w-[88px] rounded-sm  bg-white/[0.04]',
                    'flex items-center justify-center',
                    'text-content-primary cursor-pointer text-sm font-medium',
                    'transition-all duration-200 ease-linear',
                  )}
                  onClick={handleLogin}
                >
                  {t('navbar.login')}
                </div>
              ) : null}

              {/* 下载 IDE 按钮 */}
              <div
                className={clsx(
                  'box-border flex h-[32px] items-center justify-center gap-2 rounded-[6px]',
                  'backdrop-blur-[12px]',
                  'transition-all duration-200 ease-linear',
                  'h-7 w-[123px]',
                  'can-hover:hover:opacity-80 lg:h-8',
                  !systemSupported
                    ? 'can-hover:hover:border-[#71C9C0]/50 can-hover:hover:bg-white/5 cursor-not-allowed border-[#71C9C0]/50 bg-white/5 opacity-60'
                    : 'bg-background-contrast cursor-pointer',
                )}
                onClick={systemSupported ? handleDownload : handleUnsupportedClick}
                title={
                  systemSupported
                    ? t('navbar.downloadTooltipSupported')
                    : t('navbar.downloadTooltipUnsupported')
                }
              >
                <span
                  className={clsx(
                    'can-hover:hover:opcity-60 font-medium',
                    'xs:text-[clamp(12px,1.8vw,14px)] xs:leading-[clamp(14px,2vw,16px)] text-xs leading-[14px] md:text-sm lg:leading-4',
                    systemSupported ? 'text-content-contrast' : 'text-[#DAF4F4]/60',
                  )}
                >
                  {t('navbar.download')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 移动端菜单抽屉 */}
      <Drawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        placement="right"
        width="100%"
        closable={false}
        styles={{
          body: { padding: 0, background: '#0D0D0D' },
          header: { display: 'none' },
        }}
        rootClassName="mobile-menu-drawer"
      >
        <div className="bg-background-primary text-content-primary flex h-full flex-col">
          {/* 抽屉顶部：Logo + 语言切换 + 关闭按钮 */}
          <div className="flex h-16 items-center justify-between px-5 py-4">
            <div
              className="flex h-6 cursor-pointer items-center gap-5"
              onClick={() => {
                handleHome();
                setMobileMenuOpen(false);
              }}
            >
              <Logo />
            </div>
            <div className="flex items-center gap-4">
              <div
                className="text-content-primary flex cursor-pointer items-center gap-1 text-sm"
                onClick={() => {
                  const newLang = i18n.language === 'zh' ? 'en' : 'zh';
                  i18n.changeLanguage(newLang);
                }}
              >
                <I18n />
                {i18n.language === 'zh' ? '中' : 'EN'}
              </div>
              <div
                className="flex size-6 cursor-pointer items-center justify-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M1 1L15 15M15 1L1 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* 抽屉内容 */}
          <div className="flex-1 overflow-y-auto px-5">
            {/* 用户信息区域 - 仅在已登录时显示 */}
            {user ? (
              <>
                {/* 用户名和邮箱 */}
                <div className="mb-6 mt-8">
                  <div className="text-content-primary text-xl font-semibold">
                    {user.displayName || user.username || 'User'}
                  </div>
                  <div className="text-content-tertiary mt-1 text-sm">
                    {user.email || user.mail || ''}
                  </div>
                </div>

                {/* 个人设置 */}
                <div
                  className="flex cursor-pointer items-center justify-between border-t border-white/10 py-4"
                  onClick={() => {
                    handleSettings();
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="text-content-primary text-base">{t('navbar.settings')}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="14"
                    viewBox="0 0 8 14"
                    fill="none"
                  >
                    <path
                      d="M1 1L7 7L1 13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {/* 退出登录 - 仅在外部域名时显示 */}
                {!isInternal && (
                  <div
                    className="flex cursor-pointer items-center justify-between border-t border-white/10 py-4"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className="text-content-primary text-base">
                      {t('loginSuccess.logoutButton')}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="14"
                      viewBox="0 0 8 14"
                      fill="none"
                    >
                      <path
                        d="M1 1L7 7L1 13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}

                {/* 下载按钮 */}
                <div
                  className={clsx(
                    'mt-6 flex h-14 w-full items-center justify-center rounded-sm',
                    'cursor-pointer text-base font-medium',
                    systemSupported
                      ? 'bg-background-contrast text-content-contrast'
                      : 'text-content-primary bg-white/10',
                  )}
                  onClick={() => {
                    if (systemSupported) {
                      handleDownload();
                    } else {
                      handleUnsupportedClick();
                    }
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('navbar.download')}
                </div>
              </>
            ) : (
              <>
                {/* 未登录时：显示下载和登录按钮 */}
                {/* 立即下载按钮 */}
                <div
                  className={clsx(
                    'mt-4 flex h-12 w-full items-center justify-center rounded-sm',
                    'cursor-pointer text-base font-medium',
                    systemSupported
                      ? 'bg-background-contrast text-content-contrast'
                      : 'text-content-primary bg-white/10',
                  )}
                  onClick={() => {
                    if (systemSupported) {
                      handleDownload();
                    } else {
                      handleUnsupportedClick();
                    }
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('navbar.download')}
                </div>

                {/* 登录按钮 - 仅在外部域名时显示 */}
                {!isInternal && (
                  <div
                    className={clsx(
                      'mt-3 flex h-12 w-full items-center justify-center rounded-sm',
                      'cursor-pointer text-base font-medium',
                      'text-content-primary border border-white/20',
                    )}
                    onClick={() => {
                      handleLogin();
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('navbar.login')}
                  </div>
                )}
              </>
            )}

            {/* 产品和资源折叠菜单 */}
            <div className="mt-6">
              <Collapse
                ghost
                expandIconPosition="end"
                activeKey={expandedKeys}
                onChange={(keys) => setExpandedKeys(keys as string[])}
                expandIcon={({ isActive }) => (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    style={{
                      transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  >
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                items={[
                  {
                    key: 'product',
                    label: (
                      <span className="text-content-primary text-base">{t('navbar.products')}</span>
                    ),
                    children: (
                      <div className="flex flex-col gap-4 pl-4">
                        <div
                          className="text-content-primary/80 cursor-pointer text-sm"
                          onClick={() => {
                            navigate('/download');
                            setMobileMenuOpen(false);
                          }}
                        >
                          {t('navbar.productMenu.ide')}
                        </div>
                        <div
                          className="text-content-primary/80 cursor-pointer text-sm"
                          onClick={() => {
                            navigate('/download');
                            setMobileMenuOpen(false);
                          }}
                        >
                          {t('navbar.productMenu.extension')}
                        </div>
                        <div
                          className="text-content-primary/80 cursor-pointer text-sm"
                          onClick={() => {
                            navigate('/download');
                            setMobileMenuOpen(false);
                          }}
                        >
                          {t('navbar.productMenu.cli')}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'resource',
                    label: (
                      <span className="text-content-primary text-base">
                        {t('navbar.resources')}
                      </span>
                    ),
                    children: (
                      <div className="flex flex-col gap-4 pl-4">
                        <a
                          href="https://www.codeflicker.ai/docs/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-content-primary/80 cursor-pointer text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t('navbar.resourceMenu.docs')}
                        </a>
                        <div className="text-content-primary/80 cursor-pointer text-sm">
                          {t('navbar.resourceMenu.bestPractices')}
                        </div>
                        <a
                          href="https://www.codeflicker.ai/docs/changelog.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-content-primary/80 cursor-pointer text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t('navbar.resourceMenu.changelog')}
                        </a>
                      </div>
                    ),
                  },
                ]}
              />
            </div>

            {/* 价格菜单项 - 移动端 - 仅对外域名显示 */}
            {!isInternal && (
              <div
                className="flex cursor-pointer items-center justify-between border-t border-white/10 py-4"
                onClick={() => {
                  navigate('/pricing');
                  setMobileMenuOpen(false);
                }}
              >
                <span className="text-content-primary text-base">{t('navbar.pricing')}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="14"
                  viewBox="0 0 8 14"
                  fill="none"
                >
                  <path
                    d="M1 1L7 7L1 13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
}
